/**
 * Create or edit one package. Thin over `usePlanForm` - every piece of state,
 * the save-ability rule, the POST/PATCH and the error-code translation live in
 * the hook, so this file only decides what the form looks like in the CRM.
 *
 * It is the heaviest form in the app: a code, a service, two content languages
 * of name and description, four numbers, two switches, and then what the kind of
 * service asks for: the platform matrix for a platform package, the monitoring
 * matrix and the bullet editor for a Veebihaldus package (its matrix drives site
 * monitoring, docs/saidiseire-paketid.md), the bullet editor alone for the rest.
 */
import { Loader2 } from "lucide-react";
import {
  EDIT_LOCALES,
  localeText,
  makeT,
  usePlanForm,
  type ApiClient,
  type EntitlementCatalogEntry,
  type Locale,
  type PlanDTO,
  type ProductDTO,
} from "@wa/ui";
import { Btn, MenuField, Modal } from "../../../ui";
import { Field, LangSwitch, LocaleField, Pill, deriveFilledMap } from "../_shared/LocaleFields";
import { FeatureMatrix } from "./FeatureMatrix";
import { HighlightsEditor } from "./HighlightsEditor";

/** A section title inside the form - the matrix and the bullet list each get one. */
function Legend({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="mt-6">
      <div className="text-[14px] font-bold tracking-[-0.022em]">{title}</div>
      <p className="crm-mut mt-1 text-[12px] leading-[1.5]">{hint}</p>
    </div>
  );
}

export function PlanEditorModal({
  api,
  locale,
  catalog,
  monitorCatalog,
  products,
  defaultProductCode,
  lockedProductCode,
  plan,
  onClose,
  onSaved,
}: {
  api: ApiClient;
  locale: Locale;
  catalog: EntitlementCatalogEntry[];
  /** The monitoring catalog, for a package of the Veebihaldus service. */
  monitorCatalog?: EntitlementCatalogEntry[];
  products: ProductDTO[];
  /** Service a new package starts on. */
  defaultProductCode: string;
  /** Set in the service detail view: the service cannot be changed here. */
  lockedProductCode?: string;
  /** `null` = create. */
  plan: PlanDTO | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = makeT(locale);
  const f = usePlanForm({ api, locale, catalog, monitorCatalog, products, plan, defaultProductCode, lockedProductCode, onSaved });
  const title = f.isNew ? t("editor.newTitle") : t("editor.editTitle");
  const showTopRow = f.isNew || f.showProductPicker;

  return (
    <Modal
      title={title}
      label={title}
      onClose={onClose}
      // `w-`, not `max-w-`: `.crm-modal` sets `width: min(520px, 100%)`, so a
      // max-width above 520 is dead code and silently leaves the dialog narrow.
      // The rest turns the card into a column whose middle is the only part that
      // scrolls - `.crm-modal` scrolls as a whole by default, which would carry
      // the title and the save button out of reach halfway down the matrix.
      className="flex w-[min(860px,100%)] flex-col overflow-hidden"
      headExtra={plan ? <code className="crm-mut text-[12px]">{plan.code}</code> : null}
      footer={
        <>
          <Btn onClick={onClose} disabled={f.saving}>
            {t("cancel")}
          </Btn>
          <Btn tone="dark" onClick={() => void f.save()} disabled={!f.canSave}>
            {f.saving ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("save")}
          </Btn>
        </>
      }
    >
      {/* `min-h-0` is what lets this shrink below its content and actually
          scroll; a flex item defaults to `min-height: auto` and would instead
          push the footer off the bottom of the screen. */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {showTopRow ? (
          // Each field is wrapped: `Field` renders a label and a control as two
          // siblings, which a grid would otherwise place in two separate cells.
          <div className="grid gap-x-4 sm:grid-cols-2">
            {f.isNew ? (
              <div className="min-w-0">
                <Field label={t("editor.code")} hint={t("editor.codeHint")}>
                  <Pill value={f.code} onChange={f.setCode} placeholder="premium" />
                </Field>
              </div>
            ) : null}
            {f.showProductPicker ? (
              <div className="min-w-0">
                <Field label={t("products.field.product")}>
                  <MenuField
                    label={t("products.field.product")}
                    variant="form"
                    value={f.productCode}
                    onChange={f.setProductCode}
                    options={products.map((p) => ({
                      value: p.code,
                      label: `${localeText(p.name, locale)}${p.isPlatform ? ` · ${t("products.platformBadge")}` : ""}`,
                    }))}
                  />
                </Field>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="mt-5 flex justify-end">
          <LangSwitch
            active={f.activeLocale}
            onChange={f.setActiveLocale}
            locales={EDIT_LOCALES}
            filled={deriveFilledMap(EDIT_LOCALES, f.name, f.description)}
          />
        </div>
        <LocaleField
          label={t("editor.name")}
          required
          value={f.name}
          onChange={f.setName}
          activeLocale={f.activeLocale}
        />
        <LocaleField
          label={t("editor.desc")}
          as="textarea"
          rows={3}
          value={f.description}
          onChange={f.setDescription}
          activeLocale={f.activeLocale}
        />

        {/* Four numbers side by side. `min-w-0` sits on the cell AND inside
            `Pill` on the label, because a number input's intrinsic size is
            computed from the label: without it one field demands ~218px in a
            zero-width track and paints over the one next to it. */}
        <div className="grid grid-cols-2 gap-x-4 sm:grid-cols-4">
          <div className="min-w-0">
            <Field label={t("editor.priceMonthly")}>
              <Pill value={f.priceMonthly} onChange={f.setPriceMonthly} type="number" min="0" step="0.01" />
            </Field>
          </div>
          <div className="min-w-0">
            <Field label={t("editor.priceYearly")}>
              <Pill value={f.priceYearly} onChange={f.setPriceYearly} type="number" min="0" step="0.01" />
            </Field>
          </div>
          <div className="min-w-0">
            <Field label={t("editor.currency")}>
              <Pill value={f.currency} onChange={f.setCurrency} maxLength={3} placeholder="EUR" />
            </Field>
          </div>
          <div className="min-w-0">
            <Field label={t("editor.sortOrder")}>
              <Pill value={f.sortOrder} onChange={f.setSortOrder} type="number" inputMode="numeric" />
            </Field>
          </div>
        </div>

        <label className="crm-inp mt-5 cursor-pointer">
          <input type="checkbox" checked={f.isPublic} onChange={(e) => f.setIsPublic(e.target.checked)} />
          <span className="text-[13.5px]">{t("editor.isPublic")}</span>
        </label>

        {f.isPlatformProduct ? (
          <>
            <div className="max-w-[240px]">
              <Field label={t("editor.trialDays")} hint={t("editor.trialDaysHint")}>
                <Pill value={f.trialDays} onChange={f.setTrialDays} type="number" inputMode="numeric" min="0" max="90" />
              </Field>
            </div>
            <Legend title={t("editor.featuresTitle")} hint={t("editor.featuresHint")} />
            <FeatureMatrix
              grouped={f.grouped}
              valueOf={f.featureValue}
              onChange={f.setFeature}
              locale={locale}
              t={t}
            />
          </>
        ) : (
          <>
            <p className="crm-fnote">{f.isMonitoringProduct ? t("products.monitoringHint") : t("products.displayHint")}</p>
            {f.isMonitoringProduct ? (
              <>
                <Legend title={t("editor.monitorTitle")} hint={t("editor.monitorHint")} />
                <FeatureMatrix
                  grouped={f.grouped}
                  valueOf={f.featureValue}
                  onChange={f.setFeature}
                  locale={locale}
                  t={t}
                />
              </>
            ) : null}
            <Legend title={t("editor.highlightsTitle")} hint={t("editor.highlightsHint")} />
            <div className="mt-3">
              <HighlightsEditor
                lines={f.highlightLines}
                onUpdate={f.updateHighlight}
                onRemove={f.removeHighlight}
                onAdd={f.addHighlight}
                t={t}
              />
            </div>

            <label className="crm-inp mt-5 cursor-pointer">
              <input type="checkbox" checked={f.featured} onChange={(e) => f.setFeatured(e.target.checked)} />
              <span className="text-[13.5px]">{t("editor.featured")}</span>
            </label>

            <Field label={t("editor.badge")}>
              <Pill value={f.badgeText} onChange={f.setBadgeText} placeholder={t("editor.badgePlaceholder")} />
            </Field>
          </>
        )}

        {f.error ? (
          <p className="mt-4 text-[13px] font-semibold text-[var(--color-destructive)]">{f.error}</p>
        ) : null}
      </div>
    </Modal>
  );
}
